import React, { useEffect, useState, useMemo } from 'react';
import useSWR from 'swr';
import { IProductList } from 'types/orders';
import HeaderCard from '../HeaderCard/HeaderCard';
import { OrderPaymentInformation } from 'views/orders/Components';
import CheckOrAddCommentsModal from 'views/orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import { ICommentsAndObservations } from 'types/global';
import { getOrderDetailedData } from 'services/orders';
import { getOrganizationData } from 'services/bussines';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import useUsersState from 'state/useUsersState';
import { formatRoundedUpNumber } from 'utils/formatNumbers';
import './FinanceCard.scss';

/**
 * Componente que muestra la información financiera de una orden
 * @param data - Datos de la orden
 * @param refreshData - Función para refrescar los datos
 */
const FinanceCard = ({ data, refreshData, queryParams, setQueryParams }: IFinanceCard) => {
  const { users } = useUsersState();
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>();
  const { organizationId, orderId, responsibleIds, orderComments, paymentType } = data || {};
  const responsibleUsersList = useMemo(() => {
    if (!responsibleIds || !users) return [];
    return users
      .filter(user => responsibleIds?.includes(user?.id))
      .map(user => ({
        id: user?.id,
        user: user,
        orderId: Number(orderId)
      }));
  }, [responsibleIds, users, orderId]);

  const handleOpenOrCloseCommentsModal = () => setShowCommentsModal(!showCommentsModal);

  const { data: organizationInfo } = useSWR(
    showCommentsModal && organizationId ? [`organization-${organizationId}`, organizationId] : null,
    () => getOrganizationData(Number(organizationId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;
    setLoadingComments(true);
    try {
      const kamUserId = Number(organizationInfo?.assignedKam?.id);
      await submitComment({
        orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment?.comments,
        mentions: comment?.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });
      const response = await getOrderDetailedData(orderId);
      setDataComments(response?.orderComments || []);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    setDataComments(orderComments);
  }, [orderComments]);

  return (
    <div className="financeCard">
      <HeaderCard
        data={data}
        openModal={handleOpenOrCloseCommentsModal}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />
      <div className="financeInfo">
        <div className="containerValues">
          <div className="valueRow">
            <span className="valueLabel">Método de pago:</span>
            <span className="valueAmount">{paymentType}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Monto:</span>
            <span className="valueAmount">{`$${
              ['USD', 'ARS', 'BRL'].includes(data?.currency || '')
                ? formatRoundedUpNumber(Number(data?.totalUsd || 0), 2)
                : formatRoundedUpNumber(Number(data?.total || 0), 2)
            } ${['BRL', 'ARS'].includes(data?.currency || '') ? 'USD' : data?.currency}`}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Factura:</span>
            <span className="valueAmount">
              <OrderPaymentInformation orderData={data} invoiceShow={true} />
            </span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Pago:</span>
            <span className="valueAmount">
              <OrderPaymentInformation orderData={data} />
            </span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Acuerdo de pago:</span>
            <span className="valueAmount">
              <OrderPaymentInformation orderData={data} isCommittedPaymentAgreement={true} />
            </span>
          </div>
        </div>
      </div>
      {showCommentsModal && !!organizationId && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={dataComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={loadingComments}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

interface IFinanceCard {
  data: IProductList;
  refreshData: () => void;
  queryParams?: string;
  setQueryParams?: (queryParams: string) => void;
}

export default React.memo(FinanceCard, (prevProps, nextProps) => {
  return (
    prevProps.data?.orderId === nextProps.data?.orderId &&
    prevProps.data?.total === nextProps.data?.total &&
    JSON.stringify(prevProps.data?.orderComments) === JSON.stringify(nextProps.data?.orderComments)
  );
});
